import selfAssessmentsContainerIcon from "common/assets/font-awesome/self-assessments-folder.svg";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalTabbedLayout } from "common/components/layouts/NormalTabbedLayout";
import { ContentElementList } from "common/components/lists/content/ContentElementList";
import styles from "common/components/lists/content/ContentElementList.module.css";
import { examElementListFragment } from "common/components/lists/content/fragments";
import { ListContainer } from "common/components/lists/ListContainer";
import { ListItem } from "common/components/lists/ListItem";
import { MultilineText } from "common/components/MultilineText";
import { NoDataError } from "common/components/NoDataError";
import { Section } from "common/components/Section";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { useMyExamSyllabus } from "common/hooks/useMyExamSyllabus";
import { urls } from "common/urls";
import { contentProgression } from "domain/common/content-queries";
import { contentBreadcrumbFragment } from "domain/pages/breadcrumbs";
import { ContainerProgressionOverview } from "domain/progression/ContainerProgressionOverview";
import gql from "graphql-tag";
import React from "react";
import { Link, useParams } from "react-router-dom";

import {
  ContentStatus,
  FolderType,
  GetExamSyllabusPage,
  GetExamSyllabusPageVariables,
} from "__generated__/types";

const DATA_QUERY = gql`
  query GetExamSyllabusPage($examId: ID!) {
    examSyllabusById(id: $examId) {
      ...ContentBreadcrumbFragment
      ...ExamElementListFragment
      ...ContentProgressionFragment
    }
  }
  ${contentBreadcrumbFragment}
  ${examElementListFragment}
  ${contentProgression}
`;

type RouteProps = {
  examId: string;
};

export const ExamSyllabusPage: React.FC = () => {
  const { examId } = useParams<RouteProps>();

  if (!examId) {
    throw new Error("Unexpected error: Missing examId");
  }

  const { data, loadingOrError } = useSimpleQuery<
    GetExamSyllabusPage,
    GetExamSyllabusPageVariables
  >(DATA_QUERY, {
    variables: {
      examId,
    },
  });

  const { myExamSyllabi } = useMyExamSyllabus();

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.examSyllabusById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { progression, title, context } = data.examSyllabusById;

  const elementsContainingSelfAssessments =
    data.examSyllabusById.elements.filter(
      (e) =>
        e.content.__typename === "Folder" &&
        e.content.type === FolderType.SELF_ASSESSMENT_EXERCISES_CONTAINER,
    );

  const syllabusWithoutSelfAssessments = {
    ...data.examSyllabusById,
    elements: data.examSyllabusById.elements.filter(
      (e) =>
        !elementsContainingSelfAssessments
          .map((e) => e.content.id)
          .includes(e.content.id),
    ),
  };

  return (
    <NormalTabbedLayout
      title={title}
      context={context ?? []}
      navigationTabs={[
        {
          title: "Pensum",
          path: urls.exam(examId),
          content: (
            <>
              {progression &&
                myExamSyllabi
                  ?.map((e) => e.id)
                  .includes(data.examSyllabusById.id) && (
                  <ContainerProgressionOverview
                    text="Hvor mye av dette pensumet har du fullført?"
                    progression={progression}
                  />
                )}
              <Section title="Innhold">
                <ContentElementList
                  parent={syllabusWithoutSelfAssessments}
                  withGrid
                />
              </Section>
              {elementsContainingSelfAssessments.filter(
                (e) => e.content.status === ContentStatus.PUBLISHED,
              ).length > 0 && (
                <Section title="Selvtester">
                  <ListContainer withGrid>
                    {elementsContainingSelfAssessments
                      .filter(
                        (e) => e.content.status === ContentStatus.PUBLISHED,
                      )
                      .map((selfAssessmentFolder) => {
                        return (
                          <ListItem
                            key={selfAssessmentFolder.content.id}
                            className={styles.listItem}
                          >
                            <Link
                              to={urls.folder(selfAssessmentFolder.content.id)}
                            >
                              <section className={styles.listElement}>
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <img
                                    src={selfAssessmentsContainerIcon}
                                    alt="icon"
                                    style={{ width: "50px" }}
                                  />
                                  <span className={styles.text}>
                                    <h3 className={styles.title}>
                                      {selfAssessmentFolder.content.title}
                                    </h3>
                                  </span>
                                </div>
                              </section>
                            </Link>
                          </ListItem>
                        );
                      })}
                  </ListContainer>
                </Section>
              )}
            </>
          ),
        },
        {
          title: "Beskrivelse",
          path: urls.examDescription(examId),
          content: (
            <>
              {data.examSyllabusById.description ? (
                <MultilineText {...data.examSyllabusById.description} />
              ) : (
                <p>Ingen beskrivelse er oppgitt.</p>
              )}
            </>
          ),
        },
      ]}
    />
  );
};
